import { useState, useEffect } from 'react';
import { Drawer, TextField, MenuItem, Button } from '@material-ui/core';
import { IconInfoCircle, IconMapPin } from '@tabler/icons';

import { useResponsive } from '#root/hooks';
import { shippingService } from '#root/services';
import { validateDocNumber } from '#root/utils';
import ShippingDTO, { DocumentType, DocumentTypeValue } from '#root/models/Shipping';
import DrawerAppbar from '../DrawerAppbar';
import ShipmentStatusBadge from '../../views/UserShipments/ShipmentStatusBadge';
import styles from './styles.module.scss';

interface ShippingPickupDrawerProps {
  open: boolean;
  onClose: () => void;
  shipping: ShippingDTO | null;
  onSuccess: () => void;
  createMessage: {
    success: (msg: string) => void;
    error: (msg: string) => void;
  };
}

const ShippingPickupDrawer = (props: ShippingPickupDrawerProps) => {
  const { open, onClose, shipping, onSuccess, createMessage } = props;
  const { isDesktop } = useResponsive();

  const [submitting, setSubmitting] = useState(false);
  const [fullName, setFullName] = useState('');
  const [docType, setDocType] = useState<DocumentTypeValue>(DocumentType.dni);
  const [docNumber, setDocNumber] = useState('');
  const [docError, setDocError] = useState('');

  const isFormValid =
    fullName.trim().length > 0 && docNumber.length > 0 && !validateDocNumber(docType, docNumber);

  const handleClose = () => {
    onClose();
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFullName(event.target.value);
  };

  const handleDocTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newType = event.target.value as DocumentTypeValue;
    setDocType(newType);
    // Re-validate with new type
    setDocError(validateDocNumber(newType, docNumber));
  };

  const handleDocNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      setDocNumber(value);
      setDocError(validateDocNumber(docType, value));
    }
  };

  const handleSubmit = async () => {
    if (!shipping || !isFormValid) return;

    setSubmitting(true);

    const res = await shippingService.confirmPickup(shipping.id, fullName.trim(), docType, docNumber);

    if (!res.error) {
      createMessage.success(res.message || 'Recojo confirmado correctamente');
      onSuccess();
      handleClose();
    } else {
      createMessage.error(res.message || 'No se pudo confirmar el recojo');
    }

    setSubmitting(false);
  };

  useEffect(() => {
    if (open) {
      // Reset form
      setFullName('');
      setDocType(DocumentType.dni);
      setDocNumber('');
      setDocError('');
    }
  }, [open]);

  const content = (
    <div className={styles.main}>
      {/* Status Badge */}
      <div className={styles.statusHeader}>
        <ShipmentStatusBadge
          status={shipping?.current_status}
          paid_at={shipping?.paid_at}
          shipped_at={shipping?.shipped_at}
        />
      </div>

      {/* Summary */}
      <div className={styles.summary}>
        <h4>Resumen:</h4>
        <p>{shipping?.description || '---'}</p>
      </div>

      <hr />

      {/* Pickup office location */}
      {shipping?.pickup_office && (
        <div className={styles.locationBox}>
          <div className={styles.locationIcon}>
            <IconMapPin size={20} />
          </div>
          <div className={styles.locationContent}>
            <p className={styles.locationTitle}>Lugar de recojo</p>
            <p className={styles.locationName}>{shipping.pickup_office.name}</p>
            <p className={styles.locationAddress}>{shipping.pickup_office.address}</p>
          </div>
        </div>
      )}

      <hr />

      {/* Person Picking Up Form */}
      <div className={styles.formSection}>
        <h4 className={styles.formTitle}>Datos de quien recoge</h4>

        <div className={styles.formField}>
          <TextField
            fullWidth
            required
            variant="outlined"
            label="Nombre completo"
            placeholder="Juan Carlos Pérez"
            value={fullName}
            onChange={handleNameChange}
          />
        </div>

        <div className={styles.documentRow}>
          <TextField
            select
            required
            variant="outlined"
            label="Tipo Doc."
            value={docType}
            onChange={handleDocTypeChange}
          >
            <MenuItem value={DocumentType.dni}>DNI</MenuItem>
            <MenuItem value={DocumentType.ce}>CE</MenuItem>
          </TextField>

          <TextField
            required
            variant="outlined"
            label="Número de documento"
            placeholder={docType === DocumentType.dni ? '12345678' : '123456789'}
            value={docNumber}
            onChange={handleDocNumberChange}
            error={!!docError}
            helperText={docError}
            inputProps={{
              maxLength: docType === DocumentType.dni ? 8 : 12,
            }}
          />
        </div>

        <div className={styles.helperText}>
          <IconInfoCircle />
          <span>
            La persona indicada deberá presentar su documento de identidad al momento del recojo.
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!isFormValid || submitting}
        >
          {submitting ? 'Confirmando...' : 'Confirmar Recojo'}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {isDesktop ? (
        <Drawer
          anchor="right"
          open={open}
          onClose={handleClose}
          PaperProps={{
            elevation: 0,
            style: {
              background: 'var(--color-background-base)',
              width: 420,
              height: '100%',
            },
          }}
        >
          <DrawerAppbar title={`Envío #${shipping?.id || ''}`} onClose={handleClose} />
          {content}
        </Drawer>
      ) : (
        <Drawer
          anchor="right"
          open={open}
          onClose={handleClose}
          PaperProps={{
            style: {
              width: '100%',
              background: 'var(--color-background-base)',
            },
          }}
          style={{ zIndex: 1300 }}
        >
          <DrawerAppbar isMobile title={`Envío #${shipping?.id || ''}`} onClose={handleClose} />
          {content}
        </Drawer>
      )}
    </>
  );
};

export default ShippingPickupDrawer;
